import {
  Box,
  Divider,
  Typography,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  TranscriptionBlock,
  TranscriptionReader,
} from "../../types/transcription/Transcription";
import { useEffect, useState, useRef } from "react";
import {
  useTranscript,
  useTranscriptionWithPolling,
} from "../../hooks/useTranscription";
import LoadingIndicator from "../common/LoadingIndicator/LoadingIndicator";
import { JobState } from "../../types/job/Job";
import { convertSecondsToTimeDisplay } from "../../helpers/displayHelpers";
import { TranscribeButton } from "./TranscribeButton";
import { usePlaybackTime } from "../../context/PlaybackTimeContext";
import { useTranscriptionAutoScroll } from "../../hooks/useTranscriptionAutoScroll";

export interface TranscriptionViewProps {
  customerId: string;
  interactionId: string;
  className?: string;
  hasExistingTranscript?: boolean;
}

export function TranscriptionView({
  customerId,
  interactionId,
  className = "",
  hasExistingTranscript = false,
}: Readonly<TranscriptionViewProps>) {
  const [transcriptionJobId, setTranscriptionJobId] = useState<
    string | undefined
  >(undefined);

  const { isPolling, jobState } = useTranscriptionWithPolling(
    customerId,
    interactionId,
    transcriptionJobId,
  );

  if (isPolling && jobState === JobState.InProgress) {
    return <TranscriptionInProgress />;
  }

  if (jobState === JobState.Error) {
    return (
      <TranscriptionFailed
        customerId={customerId}
        interactionId={interactionId}
        hasExistingTranscript={hasExistingTranscript}
        onRetry={setTranscriptionJobId}
      />
    );
  }

  if (jobState === JobState.Complete || (hasExistingTranscript && !isPolling)) {
    return (
      <CompletedTranscription
        customerId={customerId}
        interactionId={interactionId}
        className={className}
      />
    );
  }

  return (
    <Box className={`${className} flex flex-col justify-center p-4`}>
      <Box className="mt-2">
        <TranscribeButton
          customerId={customerId}
          interactionId={interactionId}
          hasExistingTranscript={hasExistingTranscript}
          onTranscriptionStarted={setTranscriptionJobId}
        />
      </Box>
    </Box>
  );
}

function CompletedTranscription({
  customerId,
  interactionId,
  className,
}: Readonly<TranscriptionViewProps>) {
  const {
    data: transcription,
    error,
    isLoading,
  } = useTranscript(customerId, interactionId);
  const [reader, setReader] = useState<TranscriptionReader | undefined>();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const {
    currentBlockId,
    lastPlayedBlockId,
    scrollToBlock,
    enabled: autoScrollEnabled,
    setEnabled: setAutoScrollEnabled,
  } = useTranscriptionAutoScroll({
    reader,
    containerRef,
  });

  const [parseError, setParseError] = useState<string | undefined>();

  useEffect(() => {
    if (!transcription) {
      return;
    }
    try {
      const parsed = new TranscriptionReader(transcription);
      if (parsed.blocks.length === 0) {
        setParseError("Transcript loaded but contained no audio segments.");
      } else {
        setParseError(undefined);
      }
      setReader(parsed);
    } catch (e) {
      console.error("Failed to parse transcript:", e);
      setParseError(
        `Failed to parse transcript: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  }, [transcription]);

  if (error || parseError) {
    return (
      <Box className={`${className} flex flex-col p-4`}>
        <Typography variant="body1" color="error">
          {error?.message ?? parseError}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={autoScrollEnabled}
            onChange={(e) => setAutoScrollEnabled(e.target.checked)}
          />
        }
        label={"Auto-scroll"}
      />
      <Box
        ref={containerRef}
        className={`${className} pr-8`}
      >
        {isLoading && <LoadingIndicator />}
        {reader?.blocks.map((block) => (
          <TranscriptionBlockView
            key={block.id}
            className="pb-4"
            transcriptionBlock={block}
            isHighlighted={currentBlockId === block.id}
            isLastPlayed={
              lastPlayedBlockId === block.id && currentBlockId === null
            }
            autoScrollEnabled={autoScrollEnabled}
            scrollToBlock={scrollToBlock}
          />
        ))}
        {!autoScrollEnabled && (
          <Box
            sx={{
              position: "sticky",
              bottom: 16,
              display: "flex",
              justifyContent: "center",
              width: "100%",
              pointerEvents: "none",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setAutoScrollEnabled(true)}
              startIcon={<PlayArrowIcon />}
              sx={{
                pointerEvents: "auto",
                boxShadow: 3,
              }}
            >
              Resume Auto-scroll
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

interface TranscriptionBlockViewProps {
  className?: string;
  transcriptionBlock: TranscriptionBlock;
  isHighlighted: boolean;
  isLastPlayed: boolean;
  autoScrollEnabled: boolean;
  scrollToBlock: (blockRef: React.RefObject<HTMLDivElement>) => void;
}

function TranscriptionBlockView({
  className,
  transcriptionBlock,
  isHighlighted,
  isLastPlayed,
  autoScrollEnabled,
  scrollToBlock,
}: Readonly<TranscriptionBlockViewProps>) {
  const color = useUniqueColor(transcriptionBlock.speakerLabel);
  const blockRef = useRef<HTMLDivElement | null>(null);
  const { seekToTime } = usePlaybackTime();

  const handleClick = () => {
    seekToTime(transcriptionBlock.startSeconds);
  };

  // Auto-scroll to highlighted block
  useEffect(() => {
    if (isHighlighted && autoScrollEnabled) {
      scrollToBlock(blockRef);
    }
  }, [isHighlighted, autoScrollEnabled, scrollToBlock]);

  return (
    <Box
      ref={blockRef}
      data-block-id={transcriptionBlock.id}
      className={`${className} flex space-x-4 mb-4`}
      onClick={handleClick}
      sx={{
        padding: 2,
        borderRadius: 1,
        backgroundColor: isHighlighted ? "action.selected" : "transparent",
        transition: "background-color 0.3s ease",
        opacity: isLastPlayed && !isHighlighted ? 1 : isHighlighted ? 1 : 0.7,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: isHighlighted ? "action.selected" : "action.hover",
        },
      }}
    >
      <Box sx={{ width: 80, flexShrink: 0 }}>
        <Typography
          variant="subtitle1"
          color={color}
          sx={{
            fontWeight: 600,
            wordWrap: "break-word",
            overflowWrap: "break-word",
            hyphens: "auto",
          }}
        >
          {transcriptionBlock.speakerLabel}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {convertSecondsToTimeDisplay(transcriptionBlock.startSeconds)}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box className="flex-1">
        <Typography
          variant="body1"
          color="text.primary"
          sx={{
            fontWeight: isHighlighted ? 600 : 400,
            transition: "font-weight 0.3s ease",
          }}
        >
          {transcriptionBlock.text}
        </Typography>
      </Box>
    </Box>
  );
}

function useUniqueColor(identifier: string) {
  return generateUniqueColorFromString(identifier);
}

function generateUniqueColorFromString(str: string, s = 50, l = 35) {
  const hash = hashFnv1a(str);
  const GOLDEN_ANGLE_DEGREES = 137.508;
  const hue = Math.round(((hash % 360) * GOLDEN_ANGLE_DEGREES) % 360);
  return `hsl(${hue}, ${s}%, ${l}%)`;
}

function hashFnv1a(str: string) {
  let hash = 2_166_136_261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash;
}

function TranscriptionInProgress() {
  return (
    <Box className="flex justify-center p-4">
      <Typography variant="body1" color="text.secondary">
        Transcription is still in progress. Please check back later.
      </Typography>
    </Box>
  );
}

interface TranscriptionStateProps {
  customerId: string;
  interactionId: string;
}

interface TranscriptionFailedProps extends TranscriptionStateProps {
  onRetry: (newJobId: string) => void;
  hasExistingTranscript?: boolean;
}

function TranscriptionFailed({
  customerId,
  interactionId,
  onRetry,
  hasExistingTranscript = false,
}: Readonly<TranscriptionFailedProps>) {
  return (
    <Box className="flex flex-col items-center p-4 gap-3">
      <Typography variant="body1" color="error">
        An error occurred while transcribing this interaction.
      </Typography>
      <TranscribeButton
        customerId={customerId}
        interactionId={interactionId}
        hasExistingTranscript={hasExistingTranscript}
        onTranscriptionStarted={onRetry}
      />
    </Box>
  );
}
